import React, { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, List, Typography, Divider, Collapse } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { MimshakData } from "../../interfaces/DTO/mimshakData";
import { Contact } from "../../interfaces/DTO/crm/contact";
import { NewExposureMap } from "../../hooks/feeLab/usePathDragChanges";
import { formatCurrency, formatHandlerCode, formatPercentage } from "../../utils/namesFormater";
import { formatHandlerCodeUniversal } from "../../utils/handlerCodeUniversalFormatter";
import { formatAdjustedProductType } from "../../utils/enumFormatter";
import { formatProductTypeUniversal } from "../../utils/productTypeUniversalFormatter";
import { FundSumDropMap, Simulation, SimulationChangeType, SimulationMimshakData, SimulationSource, SimulationStatus } from "../../interfaces/DTO/simulation";
import { simulationMimshakDataService } from "../../services/SimulationMimshakDataService";
import SaveSimulationButton from "./Simulation/SaveSimulationButton";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import { SimulationChangeDetectionUtil, getEffectiveSavingFee, getEffectivePensionFee } from "../../services/userTask/simulationChangeDetectionUtil";
import { AdjustedProductType } from "../../interfaces/DTO/mislaka_xml_models";
import { PensionFeeExpenseType } from "../../interfaces/DTO/mislaka_xml_pension_models";
import { ManualProductType } from "../../interfaces/DTO/manualMimshakData";
import { fundService } from "../../services/fundService";
import { MimshakProductUtil } from "../../services/MimshakProductUtil";
import { MimshakPensionService } from "../../services/mimshakPensionService";
import { FundDataBasic } from "../../interfaces/DTO/FundDataBasic";
import ChangeListItem from "./ChangeListItem";
import type { InsuranceCoverageData } from "./InsuranceChangeListItem";
import InsuranceChangeListItem from "./InsuranceChangeListItem";
import UnchangedListItem from "./UnchangedListItem";
import { getBasicInsuranceSplit, getInsuranceTotalFee as getInsuranceFee } from "./InsuranceFeesByProduct";
import { checkAndFixInvestmentPathAmounts, buildIntegrityWarningText } from "../../services/simulationDataIntegrityCheck";

const DepositInstructionsDialog = lazy(() => import("./DepositInstructionsDialog"));

const PURE_RISK_INSURANCE_TYPES = new Set([
  AdjustedProductType.PURE_RISK_POLICY,
  AdjustedProductType.MORTGAGE_LIFE_INSURANCE,
  AdjustedProductType.COLLECTIVE_RISK_POLICY,
]);

const EXECUTIVE_INSURANCE_TYPES = new Set([
  AdjustedProductType.ACTIVE_MANAGERS_INSURANCE,
  AdjustedProductType.INACTIVE_MANAGERS_INSURANCE,
]);
const MANUAL_ASSET_TYPES = new Set(Object.values(ManualProductType) as number[]);

const INACTIVE_PENSION_TYPES = new Set([
  AdjustedProductType.MANDATORY_PENSION_FUND,
  AdjustedProductType.SUPPLEMENTARY_PENSION_FUND,
]);

const isExternalSourceProductType = (productType: number): boolean =>
  EXECUTIVE_INSURANCE_TYPES.has(productType as AdjustedProductType) ||
  MANUAL_ASSET_TYPES.has(productType) ||
  INACTIVE_PENSION_TYPES.has(productType as AdjustedProductType) ||
  productType != null;

interface Props {
  originalRows: MimshakData[];
  modifiedRows: MimshakData[];
  fundSumDropMap: FundSumDropMap;
  simulationItems?: SimulationMimshakData[];
  selectedPeriodType?: "REGULAR" | "PRE_ADVICE";
  simulation?: Simulation | null;
  familyId?: number;
  onUpdateSimulation?: (name: string, notes: string, status: SimulationStatus) => void;
  onNotesChange?: (notes: Record<string, string>) => void;
  onItemsReplaced?: (items: SimulationMimshakData[]) => void;
  contactColorMap?: Record<number, string>;
  contacts?: Contact[];
  newExposureMap?: NewExposureMap;
  excludedSimulationItems?: SimulationMimshakData[];
  listItemBgColor?: string;
  notesOnlyMode?: boolean;
  saveButtonMode?: "always" | "require-simulation";
  source?: SimulationSource;
  excludedOriginalRows?: MimshakData[];
  excludedModifiedRows?: MimshakData[];
  allData?: MimshakData[];
  includedTypes?: AdjustedProductType[];
  displayMode?: "gemel" | "pension";
}

type ChangeItem = import("./ChangeListItem").ChangeListItemData & { isExcluded?: boolean; insuranceCoverage?: InsuranceCoverageData };

const SummaryChangesVisual: React.FC<Props> = ({ originalRows, modifiedRows, fundSumDropMap, simulationItems: simulationItemsProp, selectedPeriodType = "REGULAR", simulation, familyId, onUpdateSimulation, onNotesChange, onItemsReplaced, contactColorMap = {}, contacts = [], newExposureMap = {}, excludedSimulationItems = [], listItemBgColor, notesOnlyMode = false, saveButtonMode, source, excludedOriginalRows = [], excludedModifiedRows = [], allData, includedTypes, displayMode = "gemel" }) => {
  const [editingNoteKey, setEditingNoteKey] = useState<string | null>(null);
  const [depositDialogOpen, setDepositDialogOpen] = useState(false);

  const hasDeposits = useMemo(() => {
    const hasExplicitDeposit = Object.values(fundSumDropMap).some((a) => (a.deposit ?? 0) > 0);
    const originalPolicies = new Set(originalRows.map((r) => r.policy_number));
    const hasNewPolicies = modifiedRows.some((r) => !originalPolicies.has(r.policy_number));
    return hasExplicitDeposit || hasNewPolicies;
  }, [fundSumDropMap, originalRows, modifiedRows]);
  const [noteValues, setNoteValues] = useState<Record<string, string>>({});
  const userEditedKeys = useRef<Set<string>>(new Set());
  const [expandedUnchangedGroups, setExpandedUnchangedGroups] = useState<Set<string>>(new Set());
  const [expandedExcluded, setExpandedExcluded] = useState(false);

  const handleNoteBlur = (policyNumber: string) => {
    setEditingNoteKey(null);
    if (!simulation?.id || !userEditedKeys.current.has(policyNumber)) return;
    const item = simulationItemsProp?.find(i => i.policy_number === policyNumber);
    if (!item?.id) return;
    const note = noteValues[policyNumber] ?? "";
    simulationMimshakDataService.partialUpdateMany([{ id: item.id, note }]).catch(console.error);
  };

  const changeItems = useMemo(() => {
    const excludedKeys = new Set(excludedOriginalRows.map(r => `${r.policy_number}|${r.handler_code}`));
    const baseItems = simulationItemsProp
      ? simulationItemsProp.filter(i => !excludedKeys.has(`${i.policy_number}|${i.handler_code}`))
      : SimulationChangeDetectionUtil.convertModifiedToSimulationItems(originalRows, modifiedRows, fundSumDropMap);
    const excItems: Array<SimulationMimshakData & { _isExcluded?: boolean }> = simulationItemsProp && excludedSimulationItems.length > 0
      ? excludedSimulationItems.map(simItem => {
          const modified = excludedModifiedRows.find(r => r.policy_number === simItem.policy_number && r.handler_code === simItem.handler_code);
          if (!modified) return simItem;
          return {
            ...simItem,
            ...modified,
            change_types: (modified as any)._changeTypes ?? simItem.change_types,
          };
        })
      : excludedModifiedRows.map(r => ({
          ...r,
          simulation_id: undefined as any,
          change_types: (r as any)._changeTypes ?? [SimulationChangeType.UNCHANGED],
        }));
    const items: Array<SimulationMimshakData & { _isExcluded?: boolean }> = [
      ...baseItems,
      ...excItems.map(i => ({ ...i, _isExcluded: true })),
    ];

    const result: ChangeItem[] = [];

    for (const item of items) {
      // Skip unchanged unless excluded or has requested exposure changes
      if (
        item.change_types.length === 1 &&
        item.change_types[0] === SimulationChangeType.UNCHANGED &&
        !newExposureMap[item.policy_number] &&
        !(item as any)._isExcluded
      ) {
        continue;
      }

      const original = originalRows.find((r) => r.policy_number === item.policy_number && r.handler_code === item.handler_code)
        ?? originalRows.find((r) => r.policy_number === item.policy_number)
        ?? excludedOriginalRows.find((r) => r.policy_number === item.policy_number && r.handler_code === item.handler_code)
        ?? excludedOriginalRows.find((r) => r.policy_number === item.policy_number);
      const modified = modifiedRows.find((r) => r.policy_number === item.policy_number && r.handler_code === item.handler_code)
        ?? modifiedRows.find((r) => r.policy_number === item.policy_number)
        ?? ((item as any)._isExcluded ? (item as unknown as MimshakData) : undefined);

      const isHandlerChanged = !!original && original.handler_code !== item.handler_code;

      // Fund change
      let fundChange: FundDataBasic[] | "NO_FUND_SELECTED" | null = null;
      let removedFunds: FundDataBasic[] = [];
      const originalFunds: FundDataBasic[] = original
        ? (original.data?.products ?? []).flatMap(p => MimshakProductUtil.getMergedPathsFromProduct(p)).map(path => {
            const fund = fundService.getFundFromCacheById(path.path_code);
            if (!fund) return { fund_id: path.path_code, fund_name: path.path_name ?? String(path.path_code), _amount: path.accumulated_amount ?? 0, _depositPct: path.deposit_percentage } as any;
            return { ...fund, _amount: path.accumulated_amount ?? 0, _depositPct: path.deposit_percentage };
          })
        : [];
      if (modified) {
        const { funds: modifiedFunds, removedFunds: removed, noFundSelected } = SimulationChangeDetectionUtil.resolveFundChange(original, modified);
        if (noFundSelected) {
          fundChange = "NO_FUND_SELECTED";
        } else if (modifiedFunds.length > 0) {
          fundChange = modifiedFunds as FundDataBasic[];
        }
        removedFunds = removed as FundDataBasic[];
      }

      // Deposit info — read directly from item fields (set at save time) or fundSumDropMap for live display
      let depositInfo: string | null = null;
      const additions = fundSumDropMap[item.policy_number];
      const parts: string[] = [];
      // Live mode: use fundSumDropMap. Loaded simulation: use item fields (fundSumDropMap is empty).
      const depositAmount = additions?.deposit || item.deposit_amount;
      const withdrawalAmount = additions?.withdrawal || item.withdrawal_amount;
      if (depositAmount) parts.push(`הפקדה: ${formatCurrency(depositAmount)}`);
      const recurrentAmount = additions?.recurrent || item.recurrent_deposit;
      if (recurrentAmount) {
        const originalRecurrent = original?.recurrent_deposit ?? 0;
        const modifiedRecurrent = recurrentAmount;
        if (originalRecurrent !== modifiedRecurrent) {
          const isNewRecurrent = originalRecurrent === 0 && modifiedRecurrent > 0;
          if (isNewRecurrent) {
            parts.push(`ה.קבע חדשה: ${formatCurrency(modifiedRecurrent)}`);
          } else {
            parts.push(`ה.קבע: ${formatCurrency(modifiedRecurrent)} ← ${formatCurrency(originalRecurrent)}`);
          }
        }
      }
      if (additions?.transfer) parts.push(`ניוד: ${formatCurrency(additions.transfer)}`);
      if (withdrawalAmount) parts.push(`משיכה: -${formatCurrency(withdrawalAmount)}`);
      if (parts.length > 0) depositInfo = parts.join(" | ");

      // Funds sum — modifiedRows.funds_sum already includes extra from applyExtraToModifiedRow
      const fundsSum = modified?.funds_sum ?? original?.funds_sum ?? null;
      const isHandlerTransfer = (item.change_types.includes(SimulationChangeType.CLOSE_AND_MOVE) || item.change_types.includes(SimulationChangeType.PARTIAL_TRANSFER)) && !!original;
      const originalFundsSum = original?.funds_sum != null && fundsSum != null && fundsSum !== original.funds_sum
        ? original.funds_sum
        : null;

      const transfer = modified?.data?.products?.[0]?.policies_or_accounts?.[0]?.funds_transfer?.[0];


      // Equity exposure change note
      let exposureNote: string | null = null;
      if (modified) {
        const modifiedPaths = MimshakProductUtil.getMergedPathsFromProduct(modified.data?.products?.[0]);
        const originalPaths2 = original ? MimshakProductUtil.getMergedPathsFromProduct(original.data?.products?.[0]) : [];

        const avgExposure = (paths: typeof modifiedPaths) => {
          let totalAmount = 0;
          let weightedExposure = 0;
          for (const p of paths) {
            const f = fundService.getFundFromCacheById(p.path_code);
            if (f && p.accumulated_amount > 0) {
              const pct = f.stock_market_percent ?? (f.stock_market_exposure && f.total_assets ? (f.stock_market_exposure / f.total_assets) * 100 : null);
              if (pct != null) {
                weightedExposure += pct * p.accumulated_amount;
                totalAmount += p.accumulated_amount;
              }
            }
          }
          return totalAmount > 0 ? weightedExposure / totalAmount : null;
        };

        const origExposure = avgExposure(originalPaths2);
        const modExposure = avgExposure(modifiedPaths);
        if (origExposure != null && modExposure != null && Math.abs(origExposure - modExposure) >= 0.5) {
          exposureNote = `שינוי בחשיפה למניות: ${origExposure.toFixed(0)}% ← ${modExposure.toFixed(0)}%`;
        }
      }

      // Liquidity note
      let liquidityNote: string | null = null;
      const policy = modified?.data?.products?.[0]?.policies_or_accounts?.[0];
      if (policy) {
        const liquiditySummaries = policy.liquidity_summaries ?? [];
        const summaryWithDate = liquiditySummaries.find(s => s.liquidity_date);
        const liquidityDateStr = summaryWithDate?.liquidity_date;
        if (liquidityDateStr) {
          const liquidityYear = new Date(liquidityDateStr).getFullYear();
          const currentYear = new Date().getFullYear();
          const yearsUntilLiquid = liquidityYear - currentYear;
          if (yearsUntilLiquid > 6) {
            liquidityNote = `המוצר נזיל בטווח הרחוק (עוד ${yearsUntilLiquid} שנים)`;
          }
        }
      }

      // Executive transfer note
      let executiveTransferNote: string | null = null;
      if (transfer && isExternalSourceProductType(transfer.transferring_fund_id)) {
        const isExecutive = EXECUTIVE_INSURANCE_TYPES.has(transfer.transferring_fund_id as AdjustedProductType)
          || formatProductTypeUniversal(transfer.transferring_fund_id).includes("ביטוח מנהלים");
        if (isExecutive) {
          const isCapitalOnly = !!(modified as any)?._capitalOnly;
          const noteParts: string[] = ["ניוד ביטוח מנהלים"];
          if (isCapitalOnly) noteParts.push("רכיב הוני בלבד");
          const policyObj = modified?.data?.products?.[0]?.policies_or_accounts?.[0];
          // const sourceFeeDetails = policyObj?.management_fee_details ?? [];
          // const sourceFees = sourceFeeDetails
          //   .filter((m: any) => m.expense_type === 1 && m.management_fee_rate !== 0)
          //   .map((m: any) => m.management_fee_rate as number)
          //   .filter((v: number, i: number, arr: number[]) => arr.indexOf(v) === i);
          // const sourceFee = sourceFees.length > 0 ? sourceFees[0] : null;
          // const newFee = modified?.saving_fee ?? null;
          // if (sourceFee != null && newFee != null && newFee < sourceFee) {
          //   noteParts.push(`להורדת דמי ניהול: מ־${formatPercentage(sourceFee)} ל־${formatPercentage(newFee)}`);
          // }
          if (!isCapitalOnly && modified?.data?.products) {
            const factors = MimshakPensionService.getUniqueGuaranteedConversionFactors(modified.data.products);
            if (factors.length > 0) noteParts.push(`בניוד מאבדים את המקדם המובטח: ${factors.join(", ")}`);
          }
          executiveTransferNote = noteParts.join(" | ");
        } else if (
          transfer.transferring_fund_id === AdjustedProductType.MANDATORY_PENSION_FUND ||
          transfer.transferring_fund_id === AdjustedProductType.SUPPLEMENTARY_PENSION_FUND
        ) {
          const sourceTypeName = formatProductTypeUniversal(transfer.transferring_fund_id);
          executiveTransferNote = `ניוד ${sourceTypeName} לא פעילה`;
        }
      }

      const exposureEntry = newExposureMap[item.policy_number];
      const requestedExposureChanges: { label: string; oldValue: number; newValue: number; actualValue: number | null }[] = [];
      if (exposureEntry) {
        const { stock, currency, foreign } = SimulationChangeDetectionUtil.calcActualExposures(original, modified);
        if (exposureEntry.stock_market_percent) {
          requestedExposureChanges.push({ label: "מניות", oldValue: exposureEntry.stock_market_percent.old_value, newValue: exposureEntry.stock_market_percent.new_value, actualValue: stock });
        }
        if (exposureEntry.foreign_currency_exposure_percent) {
          requestedExposureChanges.push({ label: "מט״ח", oldValue: exposureEntry.foreign_currency_exposure_percent.old_value, newValue: exposureEntry.foreign_currency_exposure_percent.new_value, actualValue: currency });
        }
        if (exposureEntry.foreign_exposure_percent) {
          requestedExposureChanges.push({ label: "חו״ל", oldValue: exposureEntry.foreign_exposure_percent.old_value, newValue: exposureEntry.foreign_exposure_percent.new_value, actualValue: foreign });
        }
      }

      // Insurance coverage (PURE_RISK / MORTGAGE / COLLECTIVE)
      const adjType = (modified?.adjusted_product_type ?? original?.adjusted_product_type ?? item.adjusted_product_type) as AdjustedProductType | undefined;
      let insuranceCoverage: InsuranceCoverageData | undefined;
      let insuranceChangeTypes = item.change_types;
      if (adjType != null && PURE_RISK_INSURANCE_TYPES.has(adjType) && original && modified) {
        const origSplit = getBasicInsuranceSplit(original);
        const modSplit = getBasicInsuranceSplit(modified);
        const origCost = getInsuranceFee(original);
        const modCost = getInsuranceFee(modified);
        insuranceCoverage = {
          primaryBefore: origSplit.primary,
          primaryAfter: modSplit.primary,
          secondaryBefore: origSplit.secondary,
          secondaryAfter: modSplit.secondary,
          monthlyCostBefore: origCost,
          monthlyCostAfter: modCost,
        };
        // Compute change types for insurance products
        const computedTypes: SimulationChangeType[] = [];
        if (isHandlerChanged) computedTypes.push(SimulationChangeType.INSURANCE_HANDLER_CHANGE);
        if (modSplit.primary > origSplit.primary || modSplit.secondary > origSplit.secondary) computedTypes.push(SimulationChangeType.INSURANCE_COVERAGE_INCREASE);
        if (computedTypes.length > 0) insuranceChangeTypes = computedTypes;
        else if (insuranceChangeTypes.every(ct => ct === SimulationChangeType.UNCHANGED)) {
          // no changes — keep UNCHANGED so it stays filtered out upstream
        }
      }

      const isPureRisk = adjType != null && PURE_RISK_INSURANCE_TYPES.has(adjType);

      let integrityWarning: string | undefined;
      if (modified && !isPureRisk) {
        const check = checkAndFixInvestmentPathAmounts([modified]);
        if (!check.valid) integrityWarning = buildIntegrityWarningText(check.issues);
      }

      result.push({
        policyNumber: item.policy_number,
        contactId: (modified?.contact_id ?? original?.contact_id ?? item.contact_id),
        adjustedProductType: adjType,
        isExcluded: !!(item as any)._isExcluded,
        changeTypes: insuranceChangeTypes,
        handlerCode: item.handler_code,
        originalHandlerCode: isHandlerChanged ? (original?.handler_code ?? null) : null,
        savingFee: isPureRisk ? null : (() => {
          const newFee = modified?.saving_fee ?? item.saving_fee ?? null;
          if (displayMode === "pension") {
            const newDeposit = modified?.deposit_fee ?? item.deposit_fee ?? null;
            return getEffectivePensionFee(original, newFee, newDeposit, item.change_types);
          }
          return getEffectiveSavingFee(original?.saving_fee, newFee, isHandlerChanged);
        })(),
        allSavingFees: (() => {
          if (isPureRisk || !original) return null;
          const policy = original.data?.products?.[0]?.policies_or_accounts?.[0];
          const feeDetails = policy?.management_fee_details ?? [];
          const fees = feeDetails
            .filter((m: any) => m.expense_type === PensionFeeExpenseType.FROM_SAVINGS && m.management_fee_rate !== 0)
            .map((m: any) => m.management_fee_rate as number)
            .filter((v: number, i: number, arr: number[]) => arr.indexOf(v) === i);
          return fees.length > 1 ? fees : null;
        })(),
        originalSavingFee: isPureRisk || !original ? null : (original.saving_fee ?? null),
        depositFee: isPureRisk ? null : (() => {
          const newDeposit = modified?.deposit_fee ?? item.deposit_fee ?? null;
          if (newDeposit === 0 || newDeposit == null) return null;
          if (displayMode === "pension") {
            const newSaving = modified?.saving_fee ?? item.saving_fee ?? null;
            return getEffectivePensionFee(original, newSaving, newDeposit, item.change_types) != null ? newDeposit : null;
          }
          return newDeposit;
        })(),
        originalDepositFee: isPureRisk || !original ? null : ((original.deposit_fee ?? null) !== 0 ? (original.deposit_fee ?? null) : null),
        fundChange: isPureRisk ? null : fundChange,
        removedFunds: isPureRisk ? [] : removedFunds,
        originalFunds: isPureRisk ? [] : originalFunds,
        depositInfo,
        fundsSum: adjType != null && PURE_RISK_INSURANCE_TYPES.has(adjType) ? null : fundsSum,
        originalFundsSum: adjType != null && PURE_RISK_INSURANCE_TYPES.has(adjType) ? null : originalFundsSum,
        requestedExposureChanges,
        userNote: item.note ?? "",
        autoHighlight: [executiveTransferNote, exposureNote, liquidityNote].filter(Boolean).join(" | "),
        insuranceCoverage,
        sourceNote: (() => {
          if (!item.policy_number?.startsWith("transfered-")) return null;
          const transfer = modified?.data?.products?.[0]?.policies_or_accounts?.[0]?.funds_transfer?.[0];
          if (!transfer || !isExternalSourceProductType(transfer.transferring_fund_id)) return null;
          return `מקור: ${formatProductTypeUniversal(transfer.transferring_fund_id)}${transfer.transferring_corporation ? ` / ${formatHandlerCodeUniversal(transfer.transferring_corporation)}` : ""}`;
        })(),
        sourceProductType: (() => {
          if (!item.policy_number?.startsWith("transfered-")) return null;
          const transfer = modified?.data?.products?.[0]?.policies_or_accounts?.[0]?.funds_transfer?.[0];
          if (!transfer || !isExternalSourceProductType(transfer.transferring_fund_id)) return null;
          return formatProductTypeUniversal(transfer.transferring_fund_id);
        })(),
        sourceHandlerCode: (() => {
          if (!item.policy_number?.startsWith("transfered-")) return null;
          const transfer = modified?.data?.products?.[0]?.policies_or_accounts?.[0]?.funds_transfer?.[0];
          if (!transfer || !isExternalSourceProductType(transfer.transferring_fund_id)) return null;
          return transfer.transferring_corporation ? formatHandlerCodeUniversal(transfer.transferring_corporation) : null;
        })(),
        sourcePolicyNumber: (() => {
          if (!item.policy_number?.startsWith("transfered-")) return null;
          const transfer = modified?.data?.products?.[0]?.policies_or_accounts?.[0]?.funds_transfer?.[0];
          if (!transfer || !isExternalSourceProductType(transfer.transferring_fund_id)) return null;
          return transfer.transferring_fund_name ?? null;
        })(),
        sourceSavingFee: (() => {
          if (!item.policy_number?.startsWith("transfered-")) return null;
          const transfer = modified?.data?.products?.[0]?.policies_or_accounts?.[0]?.funds_transfer?.[0];
          if (!transfer || !isExternalSourceProductType(transfer.transferring_fund_id)) return null;
          const policy = modified?.data?.products?.[0]?.policies_or_accounts?.[0];
          const feeDetails = policy?.management_fee_details ?? [];
          const fromDetails = feeDetails
            .filter(m => m.expense_type === PensionFeeExpenseType.FROM_SAVINGS && m.management_fee_rate !== 0)
            .map(m => m.management_fee_rate)
            .filter((v): v is number => typeof v === "number")
            .filter((v, i, arr) => arr.indexOf(v) === i); // ייחודיים
          if (fromDetails.length > 0) return fromDetails;
          const paths = policy?.investment_paths ?? [];
          if (paths.length === 0) return null;
          const unique = [...new Set(paths.map(p => p.management_fee_on_savings).filter((v): v is number => typeof v === "number" && v !== 0))];
          return unique.length > 0 ? unique : null;
        })(),
        integrityWarning,
      });
    }

    return result;
  }, [originalRows, modifiedRows, fundSumDropMap, newExposureMap, simulationItemsProp, excludedOriginalRows, excludedSimulationItems, excludedModifiedRows]);

  const isSyncingNotes = useRef(false);

  // Sync computed notes back when changeItems or simulationItems update, skip user-edited keys
  useEffect(() => {
    isSyncingNotes.current = true;
    setNoteValues(prev => {
      const updates: Record<string, string> = {};
      for (const item of changeItems) {
        if (!userEditedKeys.current.has(item.policyNumber) && prev[item.policyNumber] !== item.userNote) {
          updates[item.policyNumber] = item.userNote;
        }
      }
      if (simulationItemsProp) {
        for (const simItem of simulationItemsProp) {
          const note = simItem.note ?? "";
          if (!userEditedKeys.current.has(simItem.policy_number) && !(simItem.policy_number in prev) && updates[simItem.policy_number] !== note) {
            updates[simItem.policy_number] = note;
          }
        }
      }
      if (Object.keys(updates).length === 0) {
        isSyncingNotes.current = false;
        return prev;
      }
      return { ...prev, ...updates };
    });
  }, [changeItems, simulationItemsProp]);

  const onNotesChangeRef = useRef(onNotesChange);
  useEffect(() => { onNotesChangeRef.current = onNotesChange; });

  // Bubble user notes up to parent — רק כשהשינוי הגיע מהמשתמש ולא מה-sync
  useEffect(() => {
    if (isSyncingNotes.current) {
      isSyncingNotes.current = false;
      return;
    }
    onNotesChangeRef.current?.(noteValues);
  }, [noteValues]);

  // Policies with no change — flat list, sourced from simulationItems when available
  const unchangedItems = useMemo(() => {
    const changedPolicies = new Set(changeItems.map(c => c.policyNumber));
    return originalRows
      .filter(r => !changedPolicies.has(r.policy_number))
      .map(r => {
        const simItem = simulationItemsProp?.find(s => s.policy_number === r.policy_number);
        return {
          policyNumber: r.policy_number,
          simItemId: simItem?.id ?? null,
          contactId: r.contact_id,
          productTypeLabel: r.adjusted_product_type != null ? formatAdjustedProductType(r.adjusted_product_type as AdjustedProductType) : null,
          handlerChange: formatHandlerCode(r.handler_code),
          savingFeeChange: (() => {
            const feeDetails = r.data?.products?.[0]?.policies_or_accounts?.[0]?.management_fee_details ?? [];
            const allFees = feeDetails
              .filter((m: any) => m.expense_type === 1 && m.management_fee_rate !== 0)
              .map((m: any) => m.management_fee_rate as number)
              .filter((v: number, i: number, arr: number[]) => arr.indexOf(v) === i);
            return allFees.length > 1 ? allFees.map(formatPercentage).join(", ") : formatPercentage(r.saving_fee);
          })(),
          fundsSum: r.funds_sum ?? null,
          savedNote: simItem?.note ?? "",
          integrityWarning: (() => {
            const check = checkAndFixInvestmentPathAmounts([r]);
            return check.valid ? undefined : buildIntegrityWarningText(check.issues);
          })(),
        };
      });
  }, [originalRows, changeItems, simulationItemsProp]);

  // Group by AdjustedProductType (only non-excluded)
  const grouped = useMemo(() => {
    const map = new Map<string, ChangeItem[]>();
    for (const item of changeItems) {
      if (item.isExcluded) continue;
      const key = item.adjustedProductType
        ? formatAdjustedProductType(item.adjustedProductType)
        : "אחר";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    }
    return map;
  }, [changeItems]);

  const excludedChangeItems = useMemo(() => changeItems.filter(i => i.isExcluded), [changeItems]);

  return (
    <Box sx={{ direction: "rtl" }}>
      <Box display="flex" justifyContent="flex-end" gap={1} mb={2}>
        {hasDeposits && !notesOnlyMode && (
          <Button
            variant="outlined"
            startIcon={<AccountBalanceIcon sx={{ml: 1}}/>}
            onClick={() => setDepositDialogOpen(true)}
          >
            פרטים לביצוע
          </Button>
        )}
        {saveButtonMode && (
          saveButtonMode === "always" || simulation ? (
            <SaveSimulationButton
              originalRows={originalRows}
              modifiedRows={modifiedRows}
              fundSumDropMap={fundSumDropMap}
              simulationItems={simulationItemsProp}
              newExposureMap={newExposureMap}
              selectedPeriodType={selectedPeriodType}
              contactId={originalRows[0]?.contact_id ?? excludedSimulationItems[0]?.contact_id ?? simulationItemsProp?.[0]?.contact_id}
              familyId={familyId}
              simulation={simulation}
              source={source}
              onUpdateSimulation={onUpdateSimulation}
              onItemsReplaced={onItemsReplaced}
              excludedSimulationItems={excludedSimulationItems}
              excludedOriginalRows={excludedOriginalRows}
              excludedModifiedRows={excludedModifiedRows}
              allData={allData}
              includedTypes={includedTypes}
            />
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
              יש לטעון סימולציה קיימת כדי לשמור שינויים
            </Typography>
          )
        )}
      </Box>
      <Suspense fallback={null}>
        <DepositInstructionsDialog
          open={depositDialogOpen}
          onClose={() => setDepositDialogOpen(false)}
          originalRows={originalRows}
          modifiedRows={modifiedRows}
          fundSumDropMap={fundSumDropMap}
        />
      </Suspense>
    {contacts.length > 1 && (
      <Box sx={{ display: "flex", gap: 2, mb: 1.5, flexWrap: "wrap" }}>
        {contacts.map(c => {
          const color = contactColorMap[c.id];
          if (!color) return null;
          return (
            <Box key={c.id} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box sx={{ width: 16, height: 16, borderRadius: 0.5, backgroundColor: color, flexShrink: 0 }} />
              <Typography variant="caption" fontWeight={600}>{c.first_name}</Typography>
            </Box>
          );
        })}
      </Box>
    )}
    <Box>
      {Array.from(grouped.entries()).map(([groupLabel, items], groupIdx) => (
        <Box key={groupLabel} sx={{ mb: 2 }}>
          {groupIdx > 0 && <Divider sx={{ my: 1 }} />}
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
            {groupLabel}
          </Typography>
          {(() => {
            const noteHandlers = {
              onNoteClick: (pn: string, userNote: string) => {
                if (editingNoteKey === pn) return;
                if (!(pn in noteValues)) setNoteValues(prev => ({ ...prev, [pn]: userNote }));
                setEditingNoteKey(pn);
              },
              onNoteChange: (pn: string, value: string) => {
                userEditedKeys.current.add(pn);
                setNoteValues(prev => ({ ...prev, [pn]: value }));
              },
              onNoteBlur: (pn: string) => { if (!notesOnlyMode) handleNoteBlur(pn); },
              onNoteUpdate: (pn: string, note: string) => {
                const simItem = simulationItemsProp?.find(i => i.policy_number === pn);
                if (simItem?.id) simulationMimshakDataService.partialUpdateMany([{ id: simItem.id, note }]).catch(console.error);
                setEditingNoteKey(null);
              },
              onNoteCancel: (pn: string, userNote: string) => {
                userEditedKeys.current.delete(pn);
                setNoteValues(prev => ({ ...prev, [pn]: userNote }));
                setEditingNoteKey(null);
              },
            };
            const regularItems = items.filter(item => item.adjustedProductType == null || !PURE_RISK_INSURANCE_TYPES.has(item.adjustedProductType));
            const insuranceItems = items.filter(item => item.adjustedProductType != null && PURE_RISK_INSURANCE_TYPES.has(item.adjustedProductType));
            return (
              <>
                <List dense disablePadding>
                  {regularItems.map((item) => (
                    <ChangeListItem
                      key={item.policyNumber}
                      item={item}
                      noteValue={noteValues[item.policyNumber] ?? item.userNote}
                      editingNoteKey={editingNoteKey}
                      notesOnlyMode={notesOnlyMode}
                      listItemBgColor={listItemBgColor}
                      contactColorMap={contactColorMap}
                      {...noteHandlers}
                    />
                  ))}
                </List>
                <List dense disablePadding>
                  {insuranceItems.map((item) => (
                    <InsuranceChangeListItem
                      key={item.policyNumber}
                      item={item}
                      noteValue={noteValues[item.policyNumber] ?? item.userNote}
                      editingNoteKey={editingNoteKey}
                      notesOnlyMode={notesOnlyMode}
                      listItemBgColor={listItemBgColor}
                      contactColorMap={contactColorMap}
                      {...noteHandlers}
                    />
                  ))}
                </List>
              </>
            );
          })()}
        </Box>
      ))}

      {unchangedItems.length > 0 && (
        <Box mt={1}>

          {/* פוליסות עם הערה — מוצגות תמיד */}
          {unchangedItems.some(item => !!(noteValues[item.policyNumber] ?? item.savedNote)) && (
            <Typography variant="h6" color="text.secondary" fontWeight={600} sx={{ mb: 0.5 }}>
              לשים לב
            </Typography>
          )}
          <List dense disablePadding>
            {unchangedItems.filter(item => !!(noteValues[item.policyNumber] ?? item.savedNote)).map(item => (
              <UnchangedListItem
                key={item.policyNumber}
                item={item}
                noteValue={noteValues[item.policyNumber] ?? item.savedNote}
                editingNoteKey={editingNoteKey}
                notesOnlyMode={notesOnlyMode}
                listItemBgColor={listItemBgColor}
                contactColorMap={contactColorMap}
                onNoteClick={(pn, savedNote) => {
                  if (editingNoteKey === pn) return;
                  if (!(pn in noteValues)) setNoteValues(prev => ({ ...prev, [pn]: savedNote }));
                  setEditingNoteKey(pn);
                }}
                onNoteChange={(pn, value) => {
                  userEditedKeys.current.add(pn);
                  setNoteValues(prev => ({ ...prev, [pn]: value }));
                }}
                onNoteBlur={(_pn) => {
                  if (notesOnlyMode) return;
                  setEditingNoteKey(null);
                  if (simulation?.id && item.simItemId && userEditedKeys.current.has(item.policyNumber)) {
                    simulationMimshakDataService.partialUpdateMany([{ id: item.simItemId, note: noteValues[item.policyNumber] ?? "" }]).catch(console.error);
                  }
                }}
                onNoteUpdate={(pn, note) => {
                  if (item.simItemId) simulationMimshakDataService.partialUpdateMany([{ id: item.simItemId, note }]).catch(console.error);
                  setEditingNoteKey(null);
                }}
                onNoteCancel={(pn, savedNote) => {
                  userEditedKeys.current.delete(pn);
                  setNoteValues(prev => ({ ...prev, [pn]: savedNote }));
                  setEditingNoteKey(null);
                }}
              />
            ))}
          </List>

          {/* פוליסות בלי הערה — מקופלות */}
          {unchangedItems.filter(item => !(noteValues[item.policyNumber] ?? item.savedNote)).length > 0 && (
            <Box>
              <Box
                onClick={() => setExpandedUnchangedGroups(prev => {
                  const next = new Set(prev);
                  prev.has("all") ? next.delete("all") : next.add("all");
                  return next;
                })}
                sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer", userSelect: "none", py: 0.5 }}
              >
                {expandedUnchangedGroups.has("all") ? <ExpandLessIcon fontSize="small" sx={{ color: "text.secondary" }} /> : <ExpandMoreIcon fontSize="small" sx={{ color: "text.secondary" }} />}
                <Typography variant="h6" color="text.secondary" fontWeight={600} sx={{ mb: 0.5 }}>
                  עוד {unchangedItems.filter(item => !(noteValues[item.policyNumber] ?? item.savedNote)).length} פוליסות ללא שינוי
                </Typography>
              </Box>
              <Collapse in={expandedUnchangedGroups.has("all")}>
                <List dense disablePadding>
                  {unchangedItems.filter(item => !(noteValues[item.policyNumber] ?? item.savedNote)).map(item => (
                    <UnchangedListItem
                      key={item.policyNumber}
                      item={item}
                      noteValue={noteValues[item.policyNumber] ?? item.savedNote}
                      editingNoteKey={editingNoteKey}
                      notesOnlyMode={notesOnlyMode}
                      listItemBgColor={listItemBgColor ?? "#fafafa"}
                      contactColorMap={contactColorMap}
                      onNoteClick={(pn, savedNote) => {
                        if (editingNoteKey === pn) return;
                        if (!(pn in noteValues)) setNoteValues(prev => ({ ...prev, [pn]: savedNote }));
                        setEditingNoteKey(pn);
                      }}
                      onNoteChange={(pn, value) => {
                        userEditedKeys.current.add(pn);
                        setNoteValues(prev => ({ ...prev, [pn]: value }));
                      }}
                      onNoteBlur={(_pn) => {
                        setEditingNoteKey(null);
                        if (simulation?.id && item.simItemId && userEditedKeys.current.has(item.policyNumber)) {
                          simulationMimshakDataService.partialUpdateMany([{ id: item.simItemId, note: noteValues[item.policyNumber] ?? "" }]).catch(console.error);
                        }
                      }}
                      onNoteCancel={(pn, savedNote) => {
                        userEditedKeys.current.delete(pn);
                        setNoteValues(prev => ({ ...prev, [pn]: savedNote }));
                        setEditingNoteKey(null);
                      }}
                    />
                  ))}
                </List>
              </Collapse>
            </Box>
          )}
        </Box>
      )}

      {excludedChangeItems.length > 0 && (
        <Box mt={1}>
          <Box
            onClick={() => setExpandedExcluded(prev => !prev)}
            sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer", userSelect: "none", py: 0.5 }}
          >
            {expandedExcluded ? <ExpandLessIcon fontSize="small" sx={{ color: "text.secondary" }} /> : <ExpandMoreIcon fontSize="small" sx={{ color: "text.secondary" }} />}
            <Typography variant="h6" color="text.secondary" fontWeight={600} sx={{ mb: 0.5 }}>
              {excludedChangeItems.length} פוליסות שלא נכללות בסימולציה
            </Typography>
            {(() => {
              const changedCount = excludedChangeItems.filter(i => !(i.changeTypes.length === 1 && i.changeTypes[0] === SimulationChangeType.UNCHANGED)).length;
              return changedCount > 0 ? (
                <Box sx={{ backgroundColor: "secondary.main", color: "#fff", borderRadius: "50%", minWidth: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.7rem", fontWeight: 700, px: 0.5 }}>
                  {changedCount}
                </Box>
              ) : null;
            })()}
          </Box>
          <Collapse in={expandedExcluded}>
            <List dense disablePadding>
              {excludedChangeItems.map((item) => (
                <ChangeListItem
                  key={`${item.policyNumber}|${item.handlerCode}`}
                  item={item}
                  noteValue={noteValues[item.policyNumber] ?? item.userNote}
                  editingNoteKey={editingNoteKey}
                  notesOnlyMode={notesOnlyMode}
                  listItemBgColor={listItemBgColor ?? "#fafafa"}
                  contactColorMap={contactColorMap}
                  showAdjustedProductType
                  onNoteClick={(pn, userNote) => {
                    if (editingNoteKey === pn) return;
                    if (!(pn in noteValues)) setNoteValues(prev => ({ ...prev, [pn]: userNote }));
                    setEditingNoteKey(pn);
                  }}
                  onNoteChange={(pn, value) => {
                    userEditedKeys.current.add(pn);
                    setNoteValues(prev => ({ ...prev, [pn]: value }));
                  }}
                  onNoteBlur={(pn) => { if (!notesOnlyMode) handleNoteBlur(pn); }}
                  onNoteUpdate={(pn, note) => {
                    const simItem = excludedSimulationItems.find(i => i.policy_number === pn);
                    if (simItem?.id) simulationMimshakDataService.partialUpdateMany([{ id: simItem.id, note }]).catch(console.error);
                    setEditingNoteKey(null);
                  }}
                  onNoteCancel={(pn, userNote) => {
                    userEditedKeys.current.delete(pn);
                    setNoteValues(prev => ({ ...prev, [pn]: userNote }));
                    setEditingNoteKey(null);
                  }}
                />
              ))}
            </List>
          </Collapse>
        </Box>
      )}
    </Box>
    </Box>
  );
};

export default SummaryChangesVisual;
